import {
  cancelSessionApiCall,
  cancelTotalSessionApiCall,
  changeUserSessionsNumberApiCall,
  copySessionsApiCall,
  deleteInactiveSessionsApiCall,
  deleteSessionApiCall,
  getPublicSessionsApiCall,
  getSessionsByClassIdApiCall,
  getSessionsWithinPeriodApiCall,
  saveSessionApiCall,
  updateMultipleSessionsApiRequest,
  updateSessionCapacityApiCall,
  updateSessionFromEventApiCall,
} from '@guryou/api-client-gql';
import { LocationType, SAVED_LOCATION } from '@guryou/core';
import { isValidEmail } from '@guryou/utils/build/stringUtils';

import { resetState } from '../actions';
import { execute } from '../actions/actionWrapper';
import { Action, basicReducer, handleLocationInput } from '../core';

// Types
export const SESSIONS_GET_MANY = { entity: 'SESSION', action: Action.GET_MANY };
export const CREATE_SESSION = { entity: 'SESSION', action: Action.CREATE };
export const PROVIDER_SESSIONS_GET_MANY = { entity: 'PROVIDER_SESSIONS', action: Action.GET_MANY };
export const SESSION_UPDATE = { entity: 'SESSION', action: Action.UPDATE };
export const DELETE_SESSION = { entity: 'SESSION', action: Action.DELETE };
export const CANCEL_SESSION = { entity: 'CANCEL_SESSION', action: Action.UPDATE };
export const CANCEL_TOTAL_SESSION = { entity: 'CANCEL_TOTAL_SESSION', action: Action.UPDATE };
export const USER_MEMBERSHIP_SESSIONS_NUMBER_UPDATE = { entity: 'USER_MEMBERSHIP_SESSIONS_NUMBER', action: Action.UPDATE };
export const SESSION_FROM_EVENT = { entity: 'SESSION_FROM_EVENT', action: Action.UPDATE };

export const SESSIONS_WITHIN_PERIOD = { entity: 'SESSIONS_WITHIN_PERIOD', action: Action.GET_MANY };
// Modifica sessioni N (#1713): data is [{ id, success, error }] per session.
export const SESSIONS_MULTIPLE_UPDATE = { entity: 'SESSIONS_MULTIPLE_UPDATE', action: Action.UPDATE };
export const COPY_SESSIONS = { entity: 'COPY_SESSIONS', action: Action.CREATE };

// Actions
export const saveSession =
  (data, isDeal = false) =>
  async dispatch => {
    const {
      id,
      classId,
      price,
      repeatInterval,
      duration,
      resourceId,
      latitude,
      longitude,
      locationType,
      locationNote,
      address,
      endDate,
      startDate,
      endTime,
      startTime,
      date,
      materialResource,
      workingDays,
      addedDays,
      ...rest
    } = data;

    const request = {
      id: +id || null,
      classId: +classId,
      resourceId: +resourceId,
      price: +price,
      duration: +duration,
      repeatInterval: +repeatInterval || null,
      startTime,
      endTime,
      ...rest,
    };

    if (!id) {
      request.materialResource = materialResource ? +materialResource : null;
    }

    if (workingDays) {
      const normalizedWorkingDays = Array.isArray(workingDays) ? workingDays : [workingDays];

      request.workingDays = normalizedWorkingDays.filter(day => day !== '').map(d => +d);
    }

    if (addedDays) {
      request.addedDays = addedDays.map(x => ({ ...x, resourceId: +x.resourceId }));
    }

    if (startDate) {
      request.startTime = `${startDate}T${startTime}:00.000Z`;
    }
    if (endDate) {
      request.endTime = `${endDate}T${endTime}:00.000Z`;
    }
    if (date) {
      request.startTime = `${date}T${startTime}:00.000Z`;
      request.endTime = `${date}T${endTime}:00.000Z`;
    }

    if (+locationType === LocationType.mapLocation || locationType === SAVED_LOCATION) {
      request.latitude = +latitude;
      request.longitude = +longitude;
      request.address = address;
    } else if (+locationType === LocationType.plainNote) {
      request.locationNote = locationNote;
    }

    execute(dispatch, () => saveSessionApiCall({ request, isDeal }), id ? SESSION_UPDATE : CREATE_SESSION);
  };

export const deleteSession = id => async dispatch => execute(dispatch, () => deleteSessionApiCall({ id }), DELETE_SESSION, true);

export const cancelSession = id => async dispatch => execute(dispatch, () => cancelSessionApiCall({ id }), CANCEL_SESSION, true);

export const cancelTotalSession = (sessionId, classId) => async dispatch =>
  execute(dispatch, () => cancelTotalSessionApiCall({ sessionId, classId }), CANCEL_TOTAL_SESSION, true);

export const deleteInactiveSessions = classId => async dispatch =>
  execute(dispatch, () => deleteInactiveSessionsApiCall({ classId }), DELETE_SESSION, true);

export const getPublicSessions = classId => async dispatch =>
  execute(dispatch, () => getPublicSessionsApiCall({ classId: +classId }), SESSIONS_GET_MANY, false, { classId: +classId });

export const getSessionsByClassId =
  (classId, silent = false) =>
  async dispatch =>
    execute(
      dispatch,
      () =>
        getSessionsByClassIdApiCall(
          { classId: +classId },
          { resources: ['id', 'name'], location: ['note', 'latitude', 'longitude', 'address', 'addressUrl', 'url'], materialResource: ['id', 'name'] }
        ),
      PROVIDER_SESSIONS_GET_MANY,
      false,
      {
        classId: +classId,
      },
      silent
    );

export const changeUserSessionNumber = (id, numberOfSessions, startDate, endDate, action) => async dispatch => {
  let startTime = null;
  let endTime = null;
  if (startDate && endDate) {
    startTime = `${startDate}T00:00:00.000Z`;
    endTime = `${endDate}T23:59:59.000Z`;
  }
  execute(
    dispatch,
    () => changeUserSessionsNumberApiCall({ id, numberOfSessions, startTime, endTime, action }),
    USER_MEMBERSHIP_SESSIONS_NUMBER_UPDATE
  );
};

// updateSessionFromEvent's variables from the session edit: the day and times as wall-clock
// "yyyy-MM-ddTHH:mm:00.000Z", the subscribers' e-mails to tell, the staff and the place.
const sessionFromEventRequestOf = ({
  id,
  startDate,
  startTime,
  endTime,
  subscriptions,
  resourceId = null,
  locationType,
  latitude,
  longitude,
  address,
  locationNote,
  url,
}) => {
  const subscriptionsEmails = subscriptions.length
    ? subscriptions
        .map(x => {
          const isValid = isValidEmail(x.email);
          return x.user.email || (isValid && x.email);
        })
        .filter(y => y)
    : null;
  return {
    id,
    startDate: `${startDate}T${startTime}:00.000Z`,
    startTime: `${startDate}T${startTime}:00.000Z`,
    endTime: `${startDate}T${endTime}:00.000Z`,
    subscriptionsEmails,
    resourceId,
    locationType: +locationType || null,
    ...handleLocationInput({ locationType, latitude, longitude, address, locationNote, url }),
  };
};

export const updateSessionFromEvent = edit => async dispatch =>
  execute(dispatch, () => updateSessionFromEventApiCall(sessionFromEventRequestOf(edit)), SESSION_FROM_EVENT);

// The calendar's session edit saved as ONE save (the SESSION_FROM_EVENT state, as
// updateSessionFromEvent): its own capacity first — `capacity` the new one, null back to its
// class's, undefined unchanged — then the session itself, only when something else changes
// (`moves`: a capacity-only change mustn't e-mail the subscribers that the session moved). It
// stops at the first call that fails, so a refused capacity moves nothing; the calendar closes and
// reloads once, at the end, and an error stays on the open edit. Not one transaction: when the move
// fails after the capacity was saved, the capacity stays saved. Saving it again is harmless, so
// saving the same edit again finishes it.
export const saveSessionEdit =
  ({ capacity, moves = true, ...edit }) =>
  async dispatch =>
    execute(
      dispatch,
      async () => {
        if (capacity !== undefined) {
          const saved = await updateSessionCapacityApiCall(
            capacity === null ? { sessionId: +edit.id, resetCapacity: true } : { sessionId: +edit.id, capacity: +capacity }
          );
          if (!saved?.success || !moves) return saved;
        }
        return updateSessionFromEventApiCall(sessionFromEventRequestOf(edit));
      },
      SESSION_FROM_EVENT
    );

export const updateSessionFromEventMobile = (id, startTime, endTime, subscriptions) => async dispatch => {
  const subscriptionsEmails = subscriptions?.length ? subscriptions.map(x => x.user.email) : null;
  execute(dispatch, () => updateSessionFromEventApiCall({ id, startDate: startTime, startTime, endTime, subscriptionsEmails }), SESSION_FROM_EVENT);
};

export const getSessionsWithinPeriod = (start, end, classId) => async dispatch => {
  const cId = classId && Number(classId) ? Number(classId) : null;
  execute(dispatch, () => getSessionsWithinPeriodApiCall({ startTime: start, endTime: end, classId: cId }), SESSIONS_WITHIN_PERIOD);
};

// Modifica sessioni N (#1713): each request is updateSessionFromEvent's input; saved one by one by the API.
export const updateMultipleSessions = requests => async dispatch =>
  execute(dispatch, () => updateMultipleSessionsApiRequest({ requests }), SESSIONS_MULTIPLE_UPDATE);

// Duplica sessioni (#1713): the same copy to several weeks, one `copySessions` per destination, as one
// save (stops at the first that fails). Same COPY_SESSIONS state, so the calendar reacts as before.
export const copySessionsWeeks = (sessionIds, startTime, destinations) => async dispatch => {
  const ids = sessionIds?.map(x => Number(x));
  execute(
    dispatch,
    async () => {
      let result = null;
      for (const startTimeDuplicated of destinations || []) {
        result = await copySessionsApiCall({ sessionIds: ids, startTime, startTimeDuplicated });
        if (!result?.success) return result;
      }
      return result;
    },
    COPY_SESSIONS
  );
};

export const copySessions = (sessionIds, startTime, startTimeDuplicated) => async dispatch => {
  const ids = sessionIds?.map(x => Number(x));
  execute(dispatch, () => copySessionsApiCall({ sessionIds: ids, startTime, startTimeDuplicated }), COPY_SESSIONS);
};

// Reset actions
export const resetSessionState = () => async dispatch => {
  dispatch(resetState(PROVIDER_SESSIONS_GET_MANY));
  dispatch(resetState(SESSIONS_GET_MANY));
  dispatch(resetState(CREATE_SESSION));
  dispatch(resetState(SESSION_UPDATE));
  dispatch(resetState(DELETE_SESSION));
};

export const resetSessionFromEventState = () => async dispatch => {
  dispatch(resetState(SESSION_FROM_EVENT));
};

// Reducers
export const sessionReducers = {
  all: basicReducer(PROVIDER_SESSIONS_GET_MANY),
  active: basicReducer(SESSIONS_GET_MANY),
  create: basicReducer(CREATE_SESSION),
  update: basicReducer(SESSION_UPDATE),
  delete: basicReducer(DELETE_SESSION),
  cancel: basicReducer(CANCEL_SESSION),
  cancelTotal: basicReducer(CANCEL_TOTAL_SESSION),
  userMembershipSessions: basicReducer(USER_MEMBERSHIP_SESSIONS_NUMBER_UPDATE),
  updateFromEvent: basicReducer(SESSION_FROM_EVENT),
  sessionsWithinPeriod: basicReducer(SESSIONS_WITHIN_PERIOD),
  copySessions: basicReducer(COPY_SESSIONS),
  multiUpdate: basicReducer(SESSIONS_MULTIPLE_UPDATE),
};
